import { PropertyCardGallery } from '@greystar/designsystem';
import { useAnalytics } from '@greystar/lib';
import { useEffect, useMemo, useRef } from 'react';
import { SearchListProps } from '../../../component-props';
import { encodeFiltersToUrl } from '../../FloorPlanFilters/FloorPlanFilters-helper';
import { transformPropsIntoPropertyCards } from '../search-transformer';
import { NoSearchResults } from './NoSearchResults';

export interface PropertyCardProps {
  id?: number | string;
  name?: string;
  cardUrl?: string;
  address?: string;
  isTag?: boolean;
  tagName?: string;
  carouselImages?: string[];
  propertyImage?: React.ReactElement;
  tmlp?: string;
  baseRent?: string;
  noPriceFirstText?: string;
  noPriceSecondText?: string;
  onCompareToggle?: () => void;
  isCompared?: boolean;
  isFavourite?: boolean;
  onFavouriteToggle?: () => void;
  isSearchResultCard?: boolean;
  searchResultPrice?: string;
  isComparedRequired?: boolean;
  isFavouriteRequired?: boolean;
  withImageCarousel?: boolean;
  showImageOverlay?: boolean;
  imageOverlayFirstText?: string;
  imageOverlaySecondText?: string;
  isLoading?: boolean;
  bathrooms?: number;
  bedrooms?: number;
}

export const SearchList = (props: SearchListProps): JSX.Element => {
  const { properties, isSearchListingLoading, isNoReultLoading } = props;
  const analytics = useAnalytics();
  const galleryRef = useRef<HTMLDivElement>(null);
  const clickHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const filterURL = encodeFiltersToUrl(props.filters);

  const propertyCard = useMemo(
    () => transformPropsIntoPropertyCards(properties, filterURL),
    [properties, filterURL]
  );

  const skeletonCards: PropertyCardProps[] = useMemo(
    () =>
      Array.from({ length: 9 }).map((_, index) => ({
        id: `skeleton-${index}`,
        isLoading: true,
      })),
    []
  );

  useEffect(() => {
    clickHandlerRef.current = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const cardLink = target.closest('a[data-testid="property-link"]');
      const propertyCard = target.closest('[data-testid="property-card"]');

      if (cardLink && propertyCard && galleryRef.current?.contains(propertyCard)) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const cardUrl = cardLink.getAttribute('href') || '';
        const propertyId = propertyCard.id.match(/property-card-(\d+)_/)?.[1] ?? '';
        const propertyName =
          propertyCard.querySelector('[data-testid="property-name"]')?.textContent ?? '';
        const propertyAddress =
          propertyCard.querySelector('[data-testid="property-address"]')?.textContent ?? '';

        const cards = Array.from(
          galleryRef.current.querySelectorAll('[data-testid="property-card"]')
        );
        const index = cards.indexOf(propertyCard);
        const position = index < 3 ? 'top' : index < 6 ? 'middle' : 'bottom';

        const searchTerm =
          document.querySelector<HTMLInputElement>('[data-testid="search-input"]')?.value ?? '';
        const state = searchTerm.includes(',') ? searchTerm.split(',').pop()?.trim() : '';

        const eventData = {
          event: 'property_details_entry',
          action_type: 'property card click',
          state: state,
          property_location: propertyAddress,
          property_id: propertyId,
          property_name: propertyName,
          module_name: 'search results list view',
          inpage_location: position,
          click_text: propertyName,
          click_type: 'internal',
          destination_url: cardUrl,
          search_term: searchTerm,
          price_range_filter: '',
          property_type_filter: '',
          property_features_filter: '',
          timeline_filter: '',
        };

        analytics.trackEvent('property_details_entry', eventData);

        window.open(cardUrl, '_blank');
      }
    };

    return () => {
      clickHandlerRef.current = null;
    };
  }, [analytics]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (clickHandlerRef.current) {
        clickHandlerRef.current(e);
      }
    };

    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true });
  }, []);

  if (isSearchListingLoading) {
    return <PropertyCardGallery propertyCard={skeletonCards} />;
  }

  if (isNoReultLoading) {
    return <NoSearchResults />;
  }

  return (
    <div className="property-card-gallery-wrapper" ref={galleryRef} data-testid="property-gallery">
      <PropertyCardGallery propertyCard={propertyCard} />
    </div>
  );
};
